import { defineStore } from 'pinia';
import { isEmpty, isUndefined } from 'lodash';

// enums
import jobapi from '@enums/jobapi';
import eventEnums from '@enums/event-messages';

// stores
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useScenariosStore } from '@/stores/scenarios';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useWorkpackagesStore } from '@/stores/workpackages';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useJobsStore } from '@/stores/jobs';
import { useUserNotificationsStore } from '@/stores/user-notifications';
import { useContextStore } from '@/stores/context';

// utils
import { toApiUrl } from '@/utils/api-url';

// --- Utility functions ---
// -- Utility function to create an event stream to the server for events
// Event handlers can be passed in for the various status changes
function setupEventStream({ apiUrl, handler, connectHandler = null, disconnectHandler = null }) {
  const contextStore = useContextStore();
  const initialReconnectFrequency = 0.5;
  let reconnectFrequencySeconds = initialReconnectFrequency; // first attempt to reconnect after .5 seconds
  let reconnectCount = 0;
  let source;
  let timeout;
  let isClosed = false;
  const maxRetries = 120;

  // Util for reconnection handling from https://stackoverflow.com/a/54385402
  function throttle(func) {
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        func();
      }, reconnectFrequencySeconds * 1000);
    };
  }

  // - Setup reconnection function with escalating frequency
  // attempt reconnection every `reconnectFrequencySeconds`
  const reconnectFunc = throttle(() => {
    if (isClosed) return;
    console.log('Event stream lost, attempting reconnect.');
    reconnectCount += 1;

    // Force a token refresh, as this is the main reason why eventstream connections drops
    // Only force refresh every 5th connection attempt, to avoid refreshing the token just after another refresh
    if (reconnectCount > 1 && reconnectCount % 5 === 0) {
      contextStore
        .refreshUserContext({})
        .catch(error => console.warn('[assortment] token refresh failed', error));
    }

    if (reconnectCount < maxRetries) {
      // eslint-disable-next-line no-use-before-define
      setupEventSource();
    } else {
      console.error('Max retries exceeded.');
    }

    // Doubling backoff, max of 30s.
    reconnectFrequencySeconds = Math.min(reconnectFrequencySeconds * 2, 30);
  });

  // - Function to create connection and setup handlers
  const setupEventSource = () => {
    console.log('Setting up eventSource.');
    // withCredentials sends the shared auth cookie; the request is cross-origin when embedded.
    source = new EventSource(apiUrl, { withCredentials: true });
    source.addEventListener('open', () => {
      reconnectCount = 0;
      reconnectFrequencySeconds = initialReconnectFrequency;
      if (connectHandler) connectHandler();
    });

    source.addEventListener('error', event => {
      // close the source (e.g. on token timeout)
      source.close();
      if (isClosed) return;
      console.error('Notifications: Error received in event listener.', event);

      // Use the disconnect handler - Only show this if the reconnect fails
      if (disconnectHandler && reconnectCount > 0) disconnectHandler();

      // Try and reconnect
      reconnectFunc();
    });

    source.addEventListener('message', e => {
      const message = JSON.parse(e.data);

      // blank messages are sent intermittently to keep the EventSource open
      if (isEmpty(message)) return;

      // Pass off to the actual message handler
      handler(message);
    });

    return source;
  };

  // - Initial connection attempt
  setupEventSource();

  // Reconnects replace `source`, so closing must target the current one and cancel any pending retry
  return {
    close() {
      isClosed = true;
      clearTimeout(timeout);
      source.close();
    },
  };
}

export const useEventsStore = defineStore('events', {
  state: () => ({
    eventstreamEnabled: false,
    eventStream: null,
    eventStreamWorkpackageId: 0,
    eventStreamScenarioId: 0,
    eventStreamConnectionHasProblem: false,
  }),
  actions: {
    // Call when AO unmounts. Embedded, this store lives in the shell's pinia, so without an
    // explicit stop the reconnect loop keeps calling AO's backend after the app is gone. Closing
    // also cancels a scheduled reconnect or poll.
    stopEventStream() {
      if (this.eventStream) {
        this.eventStream.close();
        this.eventStream = null;
      }
      this.eventStreamConnectionHasProblem = false;
    },

    // A stopped stream reopens even for the same ids: the store outlives an embedded AO, so a
    // remount on the scenario it left still needs its events.
    resetEventStreamWithWorkpackageId({ workpackageId }) {
      const hasChanged = workpackageId !== this.eventStreamWorkpackageId;
      if (hasChanged || !this.eventStream) {
        this.eventStreamWorkpackageId = workpackageId;
        this.resetEventStream();
      }
    },

    resetEventStreamWithScenarioId({ scenarioId }) {
      const hasChanged = scenarioId !== this.eventStreamScenarioId;
      if (hasChanged || !this.eventStream) {
        this.eventStreamScenarioId = scenarioId;
        this.resetEventStream();
      }
    },

    resetEventStream() {
      const workpackagesStore = useWorkpackagesStore();
      const scenariosStore = useScenariosStore();
      const jobsStore = useJobsStore();
      const userNotificationsStore = useUserNotificationsStore();

      if (this.eventStream) {
        this.eventStream.close();
        this.eventStream = null;
      }

      if (this.eventstreamEnabled) {
        const apiPath = `/api/events/workpackage/${this.eventStreamWorkpackageId}/scenario/${this.eventStreamScenarioId}`;
        // EventSource ignores axios's baseURL, so it needs toApiUrl to reach AO's origin embedded.
        const apiUrl = toApiUrl(apiPath);

        // -- Setup handlers for different types of messages
        const handler = message => {
          // -- If job message
          if (message.msgType === eventEnums.assortmentMessageTypes.jobUpdate) {
            jobsStore.handleJobEventMessage({ message });
            if (Object.values(jobapi.workpackageJobTypes).includes(message.jobType)) {
              workpackagesStore.updateWorkpackageJobStatus({ message });
            }

            if (Object.values(jobapi.scenarioJobTypes).includes(message.jobType)) {
              scenariosStore.updateScenarioJobAndStatus({ message });
            }
            // When the status has changed it likely means there will be new notifications to be loaded
            if (message.statusHasChanged) {
              userNotificationsStore.fetchUserNotifications({});
            }
          }
          if (message.msgType === eventEnums.assortmentMessageTypes.statusUpdate) {
            scenariosStore.updateScenarioJobAndStatus();
          }
        };
        const connectHandler = () => {
          this.eventStreamConnectionHasProblem = false;

          // On connect, should fetch the scenario status and job status again
          // (because we might have missed things while disconnected, or before we selected a scenario)
          if (!isUndefined(this.eventStreamScenarioId) && this.eventStreamScenarioId !== 0) {
            scenariosStore.updateScenarioJobAndStatus();
          }
        };

        const disconnectHandler = () => {
          this.eventStreamConnectionHasProblem = true;
        };

        // -- Open event stream for messages
        const eventStream = setupEventStream({
          apiUrl,
          handler,
          connectHandler,
          disconnectHandler,
        });
        this.eventStream = eventStream;
      } else {
        // -- Eventstreaming is disabled, setup polling instead for job statuses.
        // Note, this should only be the case during development - on live servers event streams should be used
        // Create polling function. Held as `eventStream` so stopping and resetting cancel it too.
        let pollTimeout = null;
        const pollJobs = () => {
          workpackagesStore.updateWorkpackageJobStatus({});
          pollTimeout = setTimeout(pollJobs, 20000);
        };

        console.warn('Eventstreams disabled - using polling instead');
        pollJobs();
        this.eventStream = { close: () => clearTimeout(pollTimeout) };
      }
    },
  },
});
